import React, { useEffect, useRef, useState } from 'react';

const parseValue = (val) => {
  if (val == null) return { num: 0, suffix: '' };
  const s = String(val).trim();
  // extract numeric portion (including decimal and commas)
  const numMatch = s.match(/[0-9,.]+/);
  const num = numMatch ? parseFloat(numMatch[0].replace(/,/g, '')) : 0;
  // suffix is everything after the numeric characters
  const suffix = s.replace(/[0-9,().+\-\s%]+/g, '');
  // keep percent if present
  const hasPercent = /%/.test(s);
  return { num, suffix: hasPercent ? '%' : suffix };
};

const formatNumber = (n) => {
  // Always show integers while animating (no decimals)
  const rounded = Math.round(n);
  // Use locale formatting for thousands separators
  return rounded.toLocaleString();
};

const AnimatedNumber = ({ value, duration = 1400, suffix: propSuffix }) => {
  const ref = useRef(null);
  const rafRef = useRef(null);
  const [display, setDisplay] = useState('0');
  const startedRef = useRef(false);

  useEffect(() => {
    const { num, suffix } = parseValue(value);
    const suffixToUse = propSuffix != null ? propSuffix : suffix;

    const el = ref.current;
    if (!el) return;

    const onEnter = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const start = performance.now();
      const from = 0;
      const to = num || 0;

      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // easeInOut
        const current = from + (to - from) * eased;
        setDisplay(formatNumber(current));
        if (t < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          setDisplay(formatNumber(to));
        }
      };

      rafRef.current = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) onEnter();
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration, propSuffix]);

  // compute suffix for rendering if parseValue returned one and no propSuffix
  const { suffix: parsedSuffix } = parseValue(value);
  const finalSuffix = propSuffix != null ? propSuffix : parsedSuffix;

  return (
    <span ref={ref}>
      {display}
      {finalSuffix}
    </span>
  );
};

export default AnimatedNumber;
